import { useState } from 'react';
import { useGuardedReset } from './guardedReset';
import { useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowDown, ArrowUp, ExternalLink, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Spinner } from '../../components/ui/Spinner';
import { Textarea } from '../../components/ui/textarea';
import { FOLLOW_UP_TOKENS, renderFollowUp } from '../../lib/fitnessBot/followUps';
import { DEFAULT_FOLLOWUP_WINDOW } from '../../lib/fitnessBot/followUps';
import { DEFAULT_CAPABILITIES } from '../../types/leadBot';
import { instructionsHint, instructionsLabel, INSTRUCTIONS_MAX } from '../../lib/fitnessBot/instructions';
import type { BotCapabilities, BotConfig, BotFollowUps, FollowUpStep } from '../../types/leadBot';
import { resetDemo, updateBotConfig } from './api';
import { accountKyckConnectHref } from '../../lib/kyckConnect/links';

// THE FITNESS BOT'S SETTINGS (Run N/O/P UI), a separate card on the bot settings page with
// its own save, so the automotive form above is untouched. Only shown for a saved bot.
//
// ⚠️ TWO NAMES THAT LOOK ALIKE AND ARE NOT: "Forwarding number for handovers" (above) is a
// handover SMS target. "Calls to the bot's number ring" (here) is STORED ONLY: there is no voice
// handling yet, and the bot shares the Kyck number, so a per-bot call forward cannot be
// number-level. It becomes real post-conference with dedicated numbers.

type StepDraft = FollowUpStep & { hours: string; minutes: string };

const toDraft = (s: FollowUpStep): StepDraft => ({
  ...s,
  hours: String(Math.floor(s.delayMinutes / 60)),
  minutes: String(s.delayMinutes % 60),
});
// Blank is NaN, never 0: a blank delay must not become "send immediately".
const toMinutes = (d: StepDraft): number => {
  const h = d.hours.trim() === '' ? Number.NaN : Number(d.hours);
  const m = d.minutes.trim() === '' ? Number.NaN : Number(d.minutes);
  return h * 60 + m;
};
const stepError = (d: StepDraft): string | null => {
  const total = toMinutes(d);
  if (!Number.isFinite(total) || total < 1) return 'Set a delay of at least 1 minute.';
  if (total > 60 * 24 * 30) return 'A delay cannot be more than 30 days.';
  if (d.message.trim() === '') return 'Write the message.';
  return null;
};

const PREVIEW = { FirstName: 'Alex', AssistantName: 'Sam', ClubName: 'your club', Offer: 'Try the club free for 7 days.', OfferEnds: '31 October' };

function SequenceEditor({
  title,
  help,
  steps,
  onChange,
}: {
  title: string;
  help: string;
  steps: StepDraft[];
  onChange: (next: StepDraft[]) => void;
}) {
  const update = (i: number, patch: Partial<StepDraft>) =>
    onChange(steps.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= steps.length) return;
    const next = [...steps];
    [next[i], next[j]] = [next[j]!, next[i]!];
    onChange(next);
  };
  return (
    <div className="space-y-2">
      <div>
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-muted-foreground">{help}</p>
      </div>
      {steps.map((s, i) => {
        const err = stepError(s);
        const preview = renderFollowUp(s.message, PREVIEW);
        return (
          <div key={i} className="space-y-2 rounded-md border p-3">
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={s.enabled} onCheckedChange={(v) => update(i, { enabled: v === true })} />
                Step {i + 1}
              </label>
              <div className="flex items-center gap-1 text-sm">
                after
                <Input className="h-8 w-16" inputMode="numeric" value={s.hours} onChange={(e) => update(i, { hours: e.target.value })} aria-label={`Step ${i + 1} hours`} />
                h
                <Input className="h-8 w-16" inputMode="numeric" value={s.minutes} onChange={(e) => update(i, { minutes: e.target.value })} aria-label={`Step ${i + 1} minutes`} />
                m
                <span className="text-xs text-muted-foreground">since the last message</span>
              </div>
              <div className="ml-auto flex gap-1">
                <Button variant="ghost" size="sm" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up">
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => move(i, 1)} disabled={i === steps.length - 1} aria-label="Move down">
                  <ArrowDown className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => onChange(steps.filter((_x, j) => j !== i))} aria-label="Remove step">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <Textarea rows={2} value={s.message} onChange={(e) => update(i, { message: e.target.value })} aria-label={`Step ${i + 1} message`} />
            {err ? <p className="text-xs text-rose-600">{err}</p> : null}
            <p className="text-xs text-muted-foreground">
              Preview: {preview.ok ? `"${preview.text}"` : `would NOT send (${preview.missing} cannot be filled)`}
            </p>
          </div>
        );
      })}
      <Button variant="outline" size="sm" onClick={() => onChange([...steps, toDraft({ enabled: true, delayMinutes: 24 * 60, message: '' })])}>
        <Plus className="h-4 w-4" />
        Add step
      </Button>
    </div>
  );
}

const CAPABILITY_LABELS: Record<keyof BotCapabilities, string> = {
  discussPromotions: 'May mention promotions (off: the model never sees them)',
  bookAppointments: 'May offer and book tour times',
  answerFaqs: 'May answer from the club FAQs',
  handleObjections: 'May handle objections from the club answers',
};

function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function FitnessBotSettings({ config, onSaved }: { config: BotConfig; onSaved: () => void }) {
  const [signOff, setSignOff] = useState('');
  const [disclosure, setDisclosure] = useState('');
  const [instructions, setInstructions] = useState('');
  const [capabilities, setCapabilities] = useState<BotCapabilities>(DEFAULT_CAPABILITIES);
  const [callForward, setCallForward] = useState('');
  const [noResponse, setNoResponse] = useState<StepDraft[]>([]);
  const [incomplete, setIncomplete] = useState<StepDraft[]>([]);
  const [windowStart, setWindowStart] = useState<string>(DEFAULT_FOLLOWUP_WINDOW.start);
  const [windowEnd, setWindowEnd] = useState<string>(DEFAULT_FOLLOWUP_WINDOW.end);
  const [isDemo, setIsDemo] = useState(false);
  const [demoExpiry, setDemoExpiry] = useState('');
  const [timeScale, setTimeScale] = useState('');

  // ⚠️ Run U: see guardedReset.ts — never reset over unsaved edits made in the top form's refetch.
  const storedValues = {
    signOff: config.signOff ?? '',
    disclosure: config.disclosureAnswer ?? '',
    instructions: config.instructions ?? '',
    capabilities: { ...DEFAULT_CAPABILITIES, ...(config.capabilities ?? {}) },
    callForward: config.callForwardNumber ?? '',
    noResponse: (config.followUps?.noResponse ?? []).map(toDraft),
    incomplete: (config.followUps?.incomplete ?? []).map(toDraft),
    windowStart: config.followUps?.window.start ?? DEFAULT_FOLLOWUP_WINDOW.start,
    windowEnd: config.followUps?.window.end ?? DEFAULT_FOLLOWUP_WINDOW.end,
    isDemo: config.isDemo === true,
    demoExpiry: toLocalInput(config.demoExpiresAt),
    timeScale: config.demoTimeScale ? String(config.demoTimeScale) : '',
  };
  const { dirty } = useGuardedReset({
    key: config.id,
    stored: JSON.stringify(storedValues),
    form: JSON.stringify({
      signOff, disclosure, instructions, capabilities, callForward, noResponse, incomplete,
      windowStart, windowEnd, isDemo, demoExpiry, timeScale,
    }),
    apply: () => {
      setSignOff(storedValues.signOff);
      setDisclosure(storedValues.disclosure);
      setInstructions(storedValues.instructions);
      setCapabilities(storedValues.capabilities);
      setCallForward(storedValues.callForward);
      setNoResponse(storedValues.noResponse);
      setIncomplete(storedValues.incomplete);
      setWindowStart(storedValues.windowStart);
      setWindowEnd(storedValues.windowEnd);
      setIsDemo(storedValues.isDemo);
      setDemoExpiry(storedValues.demoExpiry);
      setTimeScale(storedValues.timeScale);
    },
  });

  // The vertical is chosen in the setup's own picker (admin only, with a confirm); this card only reads it.
  const fitness = config.vertical === 'fitness';
  const allSteps = [...noResponse, ...incomplete];
  const stepsValid = allSteps.every((s) => stepError(s) === null);
  const demoExpiryMs = demoExpiry === '' ? Number.NaN : new Date(demoExpiry).getTime();
  const demoValid = !isDemo || Number.isFinite(demoExpiryMs);
  const scaleValid = timeScale.trim() === '' || (Number.isFinite(Number(timeScale)) && Number(timeScale) >= 1 && Number(timeScale) <= 10000);

  const save = useMutation({
    mutationFn: () => {
      const followUps: BotFollowUps = {
        noResponse: noResponse.map((s) => ({ enabled: s.enabled, delayMinutes: toMinutes(s), message: s.message.trim() })),
        incomplete: incomplete.map((s) => ({ enabled: s.enabled, delayMinutes: toMinutes(s), message: s.message.trim() })),
        window: { start: windowStart, end: windowEnd },
      };
      return updateBotConfig(config.id, {
        ...(fitness
          ? {
              playbook: 'new_lead_engagement' as const,
              signOff: signOff.trim(),
              disclosureAnswer: disclosure.trim(),
              instructions: instructions.trim(),
              capabilities,
              callForwardNumber: callForward.trim() || null,
              followUps,
            }
          : {}),
        isDemo,
        demoExpiresAt: isDemo ? new Date(demoExpiry).toISOString() : null,
        demoTimeScale: isDemo && timeScale.trim() !== '' ? Number(timeScale) : null,
      });
    },
    onSuccess: () => {
      toast.success('Fitness settings saved.');
      onSaved();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const reset = useMutation({
    mutationFn: () => resetDemo(config.id),
    onSuccess: (r) => {
      toast.success(`Demo reset: ${r.conversationsClosed} conversations closed, ${r.appointmentsCancelled} future tours cancelled.`);
      onSaved();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <div className="mt-6 rounded-lg border p-4">
      <h3 className="text-sm font-semibold">Fitness bot</h3>
      <div className="mt-3 space-y-5">
        {fitness ? (
          <>
            <p className="text-sm">
              <Link className="font-medium text-kyck-700 underline" to={accountKyckConnectHref(config.accountId, 'setup', 'profile')}>
                Edit the club&apos;s business profile and promotions
              </Link>{' '}
              <span className="text-muted-foreground">(the only place the assistant&apos;s facts come from)</span>
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="fb-signoff">Sign-off</Label>
                <Input id="fb-signoff" value={signOff} onChange={(e) => setSignOff(e.target.value)} placeholder="Sam, Anytime Fitness Holland Park" />
              </div>
              <div>
                <Label htmlFor="fb-callforward">Calls to the bot&apos;s number ring</Label>
                <Input id="fb-callforward" value={callForward} onChange={(e) => setCallForward(e.target.value)} placeholder="0412 345 678" />
                <p className="mt-1 text-xs text-muted-foreground">
                  Stored only. There is no voice handling yet and the bot shares the Kyck number, so this takes effect after the conference with dedicated numbers. It is NOT the handover SMS number.
                </p>
              </div>
            </div>
            <div>
              <Label htmlFor="fb-disclosure">What it says to &quot;is this a bot?&quot;</Label>
              <Textarea id="fb-disclosure" rows={2} value={disclosure} onChange={(e) => setDisclosure(e.target.value)} placeholder="Leave blank for the shipped wording." />
            </div>
            <div>
              {/* ⚠️ KYCK STAFF ONLY. Never host this field in the client portal: it reaches the prompt as
                  instructions, which is only safe because the client cannot write it. */}
              <Label htmlFor="fb-instructions">{instructionsLabel(config.assistantName)}</Label>
              <Textarea
                id="fb-instructions"
                rows={10}
                maxLength={INSTRUCTIONS_MAX}
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder="e.g. Don't push a booking in the first reply. Offer a tour around the third or fourth message, or when they show clear interest."
              />
              <p className="mt-1 text-xs text-muted-foreground">
                {instructionsHint(config.assistantName)} {instructions.length.toLocaleString()} of {INSTRUCTIONS_MAX.toLocaleString()} characters.
              </p>
              {config.instructionsUpdatedAt ? (
                <p className="mt-1 text-xs text-muted-foreground">
                  Last edited by {config.instructionsUpdatedByName ?? config.instructionsUpdatedBy ?? 'unknown'} at{' '}
                  {new Date(config.instructionsUpdatedAt).toLocaleString()}.
                </p>
              ) : null}
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium">What the assistant may do</p>
              {(Object.keys(CAPABILITY_LABELS) as Array<keyof BotCapabilities>).map((k) => (
                <label key={k} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={capabilities[k]} onCheckedChange={(v) => setCapabilities({ ...capabilities, [k]: v === true })} />
                  {CAPABILITY_LABELS[k]}
                </label>
              ))}
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium">Follow-ups</p>
                <p className="text-xs text-muted-foreground">
                  The assistant decides when it writes each reply whether a follow-up is appropriate; the scheduler only sends YOUR wording below. Tokens: {FOLLOW_UP_TOKENS.map((t) => `{{${t}}}`).join(' ')}. A step with a token that cannot be filled does not send. Opening messages and replies go at any hour; only follow-ups obey the window.
                </p>
              </div>
              <SequenceEditor title="No response" help="The lead has never replied. Starts after the opening message." steps={noResponse} onChange={setNoResponse} />
              <SequenceEditor title="Conversation incomplete" help="The lead replied, went quiet, and the assistant's last message left something open." steps={incomplete} onChange={setIncomplete} />
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <Label htmlFor="fb-win-start">Follow-up window starts (club local time)</Label>
                  <Input id="fb-win-start" type="time" value={windowStart} onChange={(e) => setWindowStart(e.target.value)} className="w-32" />
                </div>
                <div>
                  <Label htmlFor="fb-win-end">and ends</Label>
                  <Input id="fb-win-end" type="time" value={windowEnd} onChange={(e) => setWindowEnd(e.target.value)} className="w-32" />
                </div>
              </div>
            </div>
          </>
        ) : null}

        <div className="space-y-3 rounded-md border border-amber-200 bg-amber-50 p-3">
          <label className="flex items-center gap-2 text-sm font-medium">
            <Checkbox checked={isDemo} onCheckedChange={(v) => setIsDemo(v === true)} />
            Conference demo bot
          </label>
          <p className="text-xs text-amber-900">
            A demo bot can allowlist up to 600 numbers and stops sending, and stops claiming inbound texts on the shared number, the moment the end time passes. Nobody has to remember to clear anything.
          </p>
          {isDemo ? (
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <Label htmlFor="fb-demo-end">Demo ends (required)</Label>
                <Input id="fb-demo-end" type="datetime-local" value={demoExpiry} onChange={(e) => setDemoExpiry(e.target.value)} className="w-56" />
              </div>
              <div>
                <Label htmlFor="fb-scale">Follow-up time scale (optional)</Label>
                <Input id="fb-scale" inputMode="numeric" value={timeScale} onChange={(e) => setTimeScale(e.target.value)} className="w-28" placeholder="e.g. 60" />
              </div>
              {config.isDemo ? (
                <>
                  <Button variant="outline" size="sm" asChild>
                    <a href={`/lead-bot/live/${config.id}`} target="_blank" rel="noreferrer">
                      <ExternalLink className="h-4 w-4" />
                      Open booth dashboard
                    </a>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={reset.isPending}
                    onClick={() => {
                      if (window.confirm('Reset the demo? This closes every open conversation and cancels all future tours. The profile, promotions and allowlist are kept. Admins only.')) reset.mutate();
                    }}
                  >
                    Reset demo
                  </Button>
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        {dirty ? <span className="mr-3 self-center text-xs text-amber-700">Unsaved changes</span> : null}
        <Button onClick={() => save.mutate()} disabled={save.isPending || !stepsValid || !demoValid || !scaleValid}>
          {save.isPending ? <Spinner className="h-4 w-4" /> : null}
          Save fitness settings
        </Button>
      </div>
    </div>
  );
}
